// features/tutorials/components/StepContent.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Info, Lightbulb, Code } from 'lucide-react';
import type { LessonStep } from '../types';

interface StepContentProps {
    step: LessonStep;
}

export const StepContent: React.FC<StepContentProps> = ({ step }) => {
    return (
        <div className="space-y-6">
            {/* Title */}
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
            >
                <div className="flex items-center space-x-3 mb-2">
                    {step.type && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
                            {step.type === 'video' && '📹 Video Lesson'}
                            {step.type === 'interactive' && '🎮 Interactive Guide'}
                            {step.type === 'image' && '🖼️ Visual Tutorial'}
                            {step.type === 'text' && '📖 Reading'}
                        </span>
                    )}
                </div>
                <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
                    {step.title}
                </h2>
            </motion.div>

            {/* Main Content */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="prose dark:prose-invert max-w-none"
            >
                <p className="text-lg text-slate-700 dark:text-slate-300 leading-relaxed">
                    {step.content}
                </p>
            </motion.div>

            {/* Callout Box */}
            {step.callout && (
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 }}
                    className={`p-4 rounded-2xl border ${step.callout.type === 'tip'
                            ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700'
                            : step.callout.type === 'warning'
                                ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700'
                                : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700'
                        }`}
                >
                    <div className="flex items-start space-x-3">
                        {step.callout.type === 'tip' && (
                            <Lightbulb className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                        )}
                        {step.callout.type === 'warning' && (
                            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                        )}
                        {step.callout.type === 'info' && (
                            <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                        )}
                        <div>
                            <h4 className="font-semibold text-sm uppercase mb-1">
                                {step.callout.type === 'tip' ? 'Pro Tip' :
                                    step.callout.type === 'warning' ? 'Warning' : 'Important'}
                            </h4>
                            <p className={`text-sm ${step.callout.type === 'tip'
                                    ? 'text-amber-800 dark:text-amber-200'
                                    : step.callout.type === 'warning'
                                        ? 'text-red-800 dark:text-red-200'
                                        : 'text-blue-800 dark:text-blue-200'
                                }`}>
                                {step.callout.text}
                            </p>
                        </div>
                    </div>
                </motion.div>
            )}

            {/* Bullet Points */}
            {step.bulletPoints && step.bulletPoints.length > 0 && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                    className="space-y-3"
                >
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                        Key Points
                    </h3>
                    <ul className="space-y-3">
                        {step.bulletPoints.map((point: string, index: number) => (
                            <motion.li
                                key={index}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.4 + index * 0.1 }}
                                className="flex items-start space-x-3"
                            >
                                <div className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex-shrink-0 mt-2" />
                                <span className="text-slate-700 dark:text-slate-300">{point}</span>
                            </motion.li>
                        ))}
                    </ul>
                </motion.div>
            )}

            {/* Code Snippet */}
            {step.codeSnippet && (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="relative"
                >
                    <div className="flex items-center space-x-2 mb-2">
                        <Code className="w-4 h-4 text-slate-400" />
                        <span className="text-sm font-medium text-slate-500 dark:text-slate-400">
                            Code Example
                        </span>
                    </div>
                    <pre className="p-4 rounded-2xl bg-slate-900 dark:bg-slate-950 text-green-400 overflow-x-auto border border-slate-700">
                        <code>{step.codeSnippet}</code>
                    </pre>
                </motion.div>
            )}

            {/* Image */}
            {step.imageUrl && (
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.5 }}
                    className="relative rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-700 shadow-lg"
                >
                    <img
                        src={step.imageUrl}
                        alt={step.title}
                        className="w-full h-auto"
                        loading="lazy"
                    />
                </motion.div>
            )}
        </div>
    );
};